/* eslint-disable no-console */
import React, { useReducer, useEffect } from "react";
import { SitemapVersionCopyProps } from "@iventis/sitemaps";
import { Project } from "@iventis/domain-model/model/project";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { AutocompleteWithLoading } from "@iventis/components/src/autocomplete";
import { Autocomplete, TextField } from "@mui/material";
import { FormWizardTemplate } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { Body1, HorizontalGap, StyledFieldLabel } from "@iventis/styles";
import { searchProjects } from "./project-api-functions";
import { copySitemapVersion, getSitemaps } from "./sitemap-api-functions";
import { initialSitemapVersionCopyState, sitemapVersionCopyReducer } from "./admin-sitemap-version-copy-state";

export type AdminSitemapVersionCopyFormProps = SitemapVersionCopyProps & {
    instanceName: string;
    projectId: string;
};

/**
 * Admin dashboard specific sitmap version copy functionality
 */
export const AdminSitemapVersionCopyForm: React.FC<AdminSitemapVersionCopyFormProps> = ({ instanceName, projectId, sourceSitemapId, sourceSitemapVersionId, close }) => {
    const translate = useIventisTranslate();
    const [state, dispatch] = useReducer(sitemapVersionCopyReducer, initialSitemapVersionCopyState);

    // Debounced async search for projects by name
    useEffect(() => {
        const timeout = setTimeout(async () => {
            const q = state.projectQuery.trim();
            if (q.length < 2) {
                dispatch({ type: "set-available-projects", payload: [] });
                dispatch({ type: "set-searching-projects", payload: false });
                return;
            }
            dispatch({ type: "set-searching-projects", payload: true });
            try {
                const projects = await searchProjects(q, instanceName);
                dispatch({ type: "set-available-projects", payload: projects });
            } catch (err) {
                console.error("Error searching projects:", err);
            } finally {
                dispatch({ type: "set-searching-projects", payload: false });
            }
        }, 250);
        return () => clearTimeout(timeout);
    }, [state.projectQuery, instanceName]);

    const handleConfirm = async () => {
        dispatch({ type: "set-submitting", payload: true });
        try {
            await copySitemapVersion(instanceName, projectId, sourceSitemapId, sourceSitemapVersionId, state.selectedProject.id, state.selectedSitemap.id);
            dispatch({ type: "set-submitting", payload: false });
            close();
        } catch (err) {
            console.error("Error copying sitemap version:", err);
            dispatch({ type: "set-submitting", payload: false });
        }
    };

    const handleProjectChange = async (project: Project | null) => {
        dispatch({ type: "reset-project-selection" });
        dispatch({ type: "set-selected-project", payload: project });

        if (project) {
            dispatch({ type: "set-loading-sitemaps", payload: true });
            try {
                const projectSitemaps = await getSitemaps(instanceName, project.id);
                dispatch({ type: "set-sitemaps", payload: projectSitemaps });
            } catch (error) {
                console.error("Error loading sitemaps:", error);
            } finally {
                dispatch({ type: "set-loading-sitemaps", payload: false });
            }
        }
    };

    const handleSitemapChange = (sitemap: Sitemap | null) => {
        dispatch({ type: "set-selected-sitemap", payload: sitemap });
    };

    const isValid = state.selectedProject != null && state.selectedSitemap != null;

    return (
        <FormWizardTemplate
            currentStage={0}
            stages={[
                {
                    showLoadingSpinner: state.isSubmitting,
                    primaryButtonText: translate(Content.common.buttons.confirm),
                    primaryButtonCallback: () => handleConfirm(),
                    secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: close }],
                    isValid,
                    submitButtonDataTestId: "sitemap-version-copy-confirm",
                },
            ]}
            title="Copy Sitemap Version"
        >
            <Body1>Select the destination project and sitemap version where this will be copied to.</Body1>

            <HorizontalGap height="15px" />
            <StyledFieldLabel>Destination Project</StyledFieldLabel>
            <Autocomplete
                value={state.selectedProject}
                options={state.availableProjects}
                getOptionLabel={(option: Project) => option?.name ?? ""}
                isOptionEqualToValue={(option: Project, value: Project) => option?.id === value?.id}
                onChange={(_, newValue: Project | null) => handleProjectChange(newValue)}
                onInputChange={(_, newInputValue: string) => dispatch({ type: "set-project-query", payload: newInputValue })}
                loading={state.isSearchingProjects}
                loadingText="Searching projects..."
                renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField {...params} placeholder="Search projects" />
                )}
            />

            <HorizontalGap height="15px" />
            <StyledFieldLabel>Destination Sitemap</StyledFieldLabel>
            <AutocompleteWithLoading
                dataTestId="copy-destination-sitemap"
                value={state.selectedSitemap}
                options={state.sitemaps}
                onChange={handleSitemapChange}
                getOptionLabel={(option: Sitemap) => option.name}
                getOptionSelected={(option, selected) => option?.id === selected?.id}
                loadingOptions={state.isLoadingSitemaps}
                disabled={!state.selectedProject}
            />
        </FormWizardTemplate>
    );
};
